import { useTranslation } from 'react-i18next';

export type CourseAccent = 'wine' | 'verde' | 'ember' | 'gold';

export type CourseModule = {
  code: string;
  level: string;
  title: string;
  body: string;
  duration: string;
  capacity?: string;
  teacher: string;
  abendkurs?: { start: string; weeks: string };
  weekender?: { start: string; weeks: string };
  price: string;
  cta?: string;
  highlighted?: boolean;
};

export type CourseSection = {
  id: string;
  accent: CourseAccent;
  style: string;
  intro: string;
  trial?: CourseModule;
  modules: CourseModule[];
};

const SECTION_ACCENT: Record<string, CourseAccent> = {
  zouk: 'wine',
  samba: 'verde',
  lambada: 'ember',
};

const HIGHLIGHTED_CODES: Record<string, true> = {
  Trial: true,
  ADV: true,
};

type SectionEntry = Omit<CourseSection, 'accent' | 'modules' | 'trial'> & {
  trial?: CourseModule;
  modules: CourseModule[];
};

function decorateModule(m: CourseModule): CourseModule {
  return HIGHLIGHTED_CODES[m.code] ? { ...m, highlighted: true } : m;
}

export function useCourseSections(): CourseSection[] {
  const { t } = useTranslation();
  const sections = t('kurse.sections', { returnObjects: true }) as SectionEntry[];
  return sections.map((s) => ({
    ...s,
    accent: SECTION_ACCENT[s.id] ?? 'wine',
    trial: s.trial ? decorateModule(s.trial) : undefined,
    modules: s.modules.map(decorateModule),
  }));
}

export type Price = { label: string; value: string; note?: string; highlighted?: boolean };

const HIGHLIGHTED_PRICE_INDEX = 3;

export function usePrices(): Price[] {
  const { t } = useTranslation();
  const items = t('kurse.preise.items', { returnObjects: true }) as Price[];
  return items.map((p, i) => (i === HIGHLIGHTED_PRICE_INDEX ? { ...p, highlighted: true } : p));
}

export type PrivatRate = { label: string; value: string; note?: string };
export type Privat = { title: string; body: string; rates: PrivatRate[] };

export function usePrivatstunden(): Privat {
  const { t } = useTranslation();
  return {
    title: t('kurse.privatstunden.title2'),
    body: t('kurse.privatstunden.body'),
    rates: t('kurse.privatstunden.rates', { returnObjects: true }) as PrivatRate[],
  };
}
